/**
 * Controller for map functionality
 */
import Map from 'ol/Map.js';
import View from 'ol/View.js';
import { fromLonLat } from 'ol/proj.js';
import { CONFIG } from '../config/config.js';
import { LayerFactory } from '../factories/layerFactory.js';
import { formatPopulation } from '../utils/formatter.js';
import { AppState } from './appState.js';

export class MapController {
  constructor() {
    this.currentCellSize = CONFIG.defaultCellSize;

    // Create layers
    this.basemapLayers = LayerFactory.createBasemapLayers();
    const watershedLayers = LayerFactory.createWatershedLayers(this.currentCellSize);
    this.boundaryLayers = watershedLayers.boundaryLayers;
    this.pointsLayers = watershedLayers.pointsLayers;

    // Create reference layers using the new method
    this.referenceLayers = LayerFactory.createReferenceLayers();

    this.popup = LayerFactory.createPopupOverlay();

    // Initialize map
    this.initializeMap();
    this.setupEventListeners();
  }

  /**
   * Initialize the OpenLayers map
   */
  initializeMap() {
    // Get all reference layers as an array
    const referenceLayersList = Object.values(this.referenceLayers).filter(layer => layer !== null);

    this.map = new Map({
      layers: [
        ...Object.values(this.basemapLayers),
        ...referenceLayersList,
        // Watershed layers will be added on demand
      ],
      overlays: [this.popup],
      target: 'map',
      view: new View({
        center: AppState.mapCenter,
        zoom: AppState.mapZoom
      })
    });
  }

  /**
   * Set up map event listeners
   */
  setupEventListeners() {
    // Close button functionality for popup
    const popupCloser = document.getElementById('popup-closer');
    if (popupCloser) {
      popupCloser.onclick = () => {
        this.popup.setPosition(undefined);
        popupCloser.blur();
        return false;
      };
    }

    // Map click handler
    this.map.on('singleclick', (evt) => this.handleMapClick(evt));
  }

  /**
   * Handle map click events
   */
  handleMapClick(evt) {
    // Don't show popups when animation is running
    if (AppState.isPlaying) {
      this.popup.setPosition(undefined);
      return;
    }

    // Get the visible points layers based on opacity sliders
    const visiblePointsLayers = CONFIG.sigmaValues.map(sigma => {
      return {
        layer: this.pointsLayers[sigma],
        sigma: sigma
      };
    }).filter(item => item.layer && item.layer.getVisible());

    // Get visible boundary layers for debugging
    const visibleBoundaryLayers = CONFIG.sigmaValues.map(sigma => {
      return {
        layer: this.boundaryLayers[sigma],
        sigma: sigma
      };
    }).filter(item => item.layer && item.layer.getVisible());

    // Sort points layers by opacity, highest first
    visiblePointsLayers.sort((a, b) => b.layer.getOpacity() - a.layer.getOpacity());

    // Get the most visible points layer
    const activeLayerInfo = visiblePointsLayers.length > 0 ? visiblePointsLayers[0] : null;

    // Only proceed if a points layer is visible with significant opacity
    if (!activeLayerInfo || activeLayerInfo.layer.getOpacity() < 0.5) {
      this.popup.setPosition(undefined);
      return;
    }

    const view = this.map.getView();
    const viewResolution = view.getResolution();
    const viewProjection = view.getProjection();

    // Query points layer first
    const url = activeLayerInfo.layer.getSource().getFeatureInfoUrl(
      evt.coordinate,
      viewResolution,
      viewProjection,
      {
        'INFO_FORMAT': 'application/json',
        'FEATURE_COUNT': 1
      }
    );

    if (url) {
      fetch(url)
        .then(response => response.json())
        .then(data => {
          if (data.features && data.features.length > 0) {
            const feature = data.features[0];
            const nPois = feature.properties.n_pois;

            // Set popup content
            const popupContent = document.getElementById('popup-content');
            if (popupContent) {
              popupContent.innerHTML = `<p><strong>Cell Size:</strong> ${this.currentCellSize}</p>
                                       <p><strong>Sigma:</strong> ${activeLayerInfo.sigma}</p>
                                       <p><strong>POIs:</strong> ${nPois}</p>`;
              this.popup.setPosition(evt.coordinate);
            }
          } else {
            // If no points feature, try querying boundary layer for debugging
            if (visibleBoundaryLayers.length > 0) {
              const bndryUrl = visibleBoundaryLayers[0].layer.getSource().getFeatureInfoUrl(
                evt.coordinate,
                viewResolution,
                viewProjection,
                {
                  'INFO_FORMAT': 'application/json',
                  'FEATURE_COUNT': 1
                }
              );
              if (bndryUrl) {
                fetch(bndryUrl)
                  .then(r => r.json())
                  .then(bndryData => {
                    if (bndryData.features && bndryData.features.length > 0) {
                      console.log('Boundary properties:', bndryData.features[0].properties);
                    }
                  });
              }
            }
            this.popup.setPosition(undefined);
          }
        })
        .catch(() => {
          this.popup.setPosition(undefined);
        });
    }
  }

  /**
   * Hide all basemap layers
   */
  hideAllBasemaps() {
    Object.values(this.basemapLayers).forEach(layer => {
      layer.setVisible(false);
    });
  }

  /**
   * Hide all reference layers
   */
  hideAllReferenceLayers() {
    Object.values(this.referenceLayers).forEach(layer => {
      if (layer) layer.setVisible(false);
    });
  }

  /**
   * Set the visibility of a specific reference layer
   * @param {string} layerKey - The key of the reference layer to show
   */
  setReferenceLayerVisibility(layerKey) {
    // First hide all reference layers
    this.hideAllReferenceLayers();

    // If 'none' is selected, we're done (all layers are hidden)
    if (layerKey === 'none') return;

    // Show the selected layer
    if (this.referenceLayers[layerKey]) {
      this.referenceLayers[layerKey].setVisible(true);
    }
  }

  /**
   * Set the visibility of a specific layer type
   * @param {string} layerType - The type of layer ('points' or 'boundary')
   * @param {boolean} visible - Whether the layer should be visible
   */
  setLayerVisibility(layerType, visible) {
    // This method now primarily updates the UI toggle and then triggers a full opacity update
    // The actual layer visibility logic is handled by updateAllLayersOpacity and updateLayerOpacity

    // Update UI controls to match
    if (layerType === 'points') {
      const pointsToggle = document.getElementById('points-toggle');
      if (pointsToggle) pointsToggle.checked = visible;
    } else if (layerType === 'boundary') {
      const boundaryToggle = document.getElementById('boundary-toggle');
      if (boundaryToggle) boundaryToggle.checked = visible;
    }

    // Call updateAllLayersOpacity to ensure everything is in sync
    this.updateAllLayersOpacity();
  }

  /**
   * Update layer opacity and visibility
   * @param {string} sigma - The sigma value for the layer
   * @param {string} type - The type of layer ('boundary' or 'points')
   * @param {object} layersCache - The cache object (this.boundaryLayers or this.pointsLayers)
   * @param {number} opacity - The desired opacity (0-1)
   */
  updateLayerOpacity(sigma, type, layersCache, opacity) {
    let layer = layersCache[sigma];

    // Lazy create layer if needed
    if (!layer && opacity > 0) {
      layer = LayerFactory.getOrCreateWatershedLayer(sigma, type === 'boundary' ? 'bndry' : 'prpt', layersCache, this.currentCellSize);
      this.map.addLayer(layer);
    }

    if (!layer) return;

    if (opacity > 0) {
      layer.setVisible(true);
      layer.setOpacity(opacity); // AppState stores opacity as 0-1
    } else {
      layer.setVisible(false);
    }
  }

  /**
   * Update all layer opacities based on UI controls
   */
  updateAllLayersOpacity() {
    const boundaryToggle = document.getElementById('boundary-toggle');
    const pointsToggle = document.getElementById('points-toggle');

    const showBoundaries = boundaryToggle && boundaryToggle.checked;
    const showPoints = pointsToggle && pointsToggle.checked;

    // Only iterate over sigmas that have changed or are non-zero in AppState
    // For simplicity during transition, we'll iterate but use AppState
    CONFIG.sigmaValues.forEach(sigma => {
      const opacity = AppState.sigmaOpacities[sigma] || 0;

      // Update boundary layer
      if (showBoundaries && opacity > 0) {
        this.updateLayerOpacity(sigma, 'boundary', this.boundaryLayers, opacity);
      } else if (this.boundaryLayers[sigma]) {
        this.boundaryLayers[sigma].setVisible(false);
      }

      // Update points layer
      if (showPoints && opacity > 0) {
        this.updateLayerOpacity(sigma, 'points', this.pointsLayers, opacity);
      } else if (this.pointsLayers[sigma]) {
        this.pointsLayers[sigma].setVisible(false);
      }
    });

    // If POI filter is active, apply it to the visible layers
    const poiSlider = document.getElementById('poi-slider');
    const minPois = poiSlider ? parseInt(poiSlider.value, 10) : 0;
    this.updatePointsFilter(minPois);
    this.updateBoundaryFilter(minPois);

    // Update the sigma display in the header unless we're in animation mode
    if (!AppState.isPlaying) {
      const sigmaDisplay = document.getElementById('sigma-display');
      const activeSigma = AppState.getActiveSigma();
      sigmaDisplay.textContent = activeSigma;
    }
  }

  /**
   * Update points layer filter by POI count
   */
  updatePointsFilter(minPois) {
    // Apply filter to all layers in the cache, not just visible ones
    // This ensures consistency during transitions/animations
    Object.values(this.pointsLayers).forEach(layer => {
      const params = layer.getSource().getParams();
      if (minPois > 0) {
        params.CQL_FILTER = `n_pois >= ${minPois}`;
      } else {
        delete params.CQL_FILTER;
      }
      layer.getSource().updateParams(params);
    });
  }

  /**
   * Update boundary layer filter by POI count
   */
  updateBoundaryFilter(minPois) {
    // Apply filter to all layers in the cache
    Object.values(this.boundaryLayers).forEach(layer => {
      const params = layer.getSource().getParams();
      if (minPois > 0) {
        params.CQL_FILTER = `n_pois >= ${minPois}`;
      } else {
        delete params.CQL_FILTER;
      }
      layer.getSource().updateParams(params);
    });
  }

  /**
   * Search for a location using Nominatim
   * @param {string} query - The location to search for
   * @returns {Promise} - Promise resolving to search results
   */
  searchLocation(query) {
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`;

    return fetch(url, {
      headers: {
        'Accept': 'application/json',
        'User-Agent': 'Cities as Inverted Watersheds Application'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch location data: ${response.status} ${response.statusText}`);
        }
        return response.json();
      })
      .catch(error => {
        console.error('Location search failed:', error);
        // Re-throw a more user-friendly error message
        throw new Error('Unable to search for locations. Please check your network connection and try again.');
      });
  }

  /**
   * Zoom to a specific location based on its bounding box
   * @param {Object} location - The location object from Nominatim
   */
  zoomToLocation(location) {
    if (!location) return;

    // Check if the location has a bounding box
    if (location.boundingbox) {
      const bbox = [
        parseFloat(location.boundingbox[2]), // west/left
        parseFloat(location.boundingbox[0]), // south/bottom
        parseFloat(location.boundingbox[3]), // east/right
        parseFloat(location.boundingbox[1])  // north/top
      ];

      // Convert the bbox from WGS84 to the map's projection if needed
      const mapProjection = this.map.getView().getProjection();

      // Create the extent in the map's projection
      const extent = [
        fromLonLat([bbox[0], bbox[1]])[0],
        fromLonLat([bbox[0], bbox[1]])[1],
        fromLonLat([bbox[2], bbox[3]])[0],
        fromLonLat([bbox[2], bbox[3]])[1]
      ];

      // Fit the view to the bounding box with some padding
      this.map.getView().fit(extent, {
        padding: [50, 50, 50, 50],
        maxZoom: CONFIG.maxZoom
      });
    } else if (location.lat && location.lon) {
      // If there's no bounding box, center on the location with a default zoom
      const center = fromLonLat([parseFloat(location.lon), parseFloat(location.lat)]);
      this.map.getView().setCenter(center);
      this.map.getView().setZoom(10); // Default zoom level for when we don't have a bbox
    }
  }

  /**
   * Update layers based on cell size change
   * @param {number} cellSize - New cell size (100 or 1000)
   */
  updateCellSize(cellSize) {
    // Only update if the cell size has actually changed
    if (this.currentCellSize === cellSize) return;

    // Remove all cached layers from the map
    Object.values(this.boundaryLayers).forEach(layer => {
      this.map.removeLayer(layer);
    });
    Object.values(this.pointsLayers).forEach(layer => {
      this.map.removeLayer(layer);
    });

    // Update current cell size
    this.currentCellSize = cellSize;

    // Reset layer caches
    const watershedLayers = LayerFactory.createWatershedLayers();
    this.boundaryLayers = watershedLayers.boundaryLayers;
    this.pointsLayers = watershedLayers.pointsLayers;

    // Update all layers - this will trigger lazy loading of the new layers
    this.updateAllLayersOpacity();

    // Update the cell size in the app state
    AppState.cellSize = cellSize;
  }
}

export default MapController;